import type { DriverTarget } from "@skaile/workspaces/plugin-registry";
import type { Logger } from "@skaile/workspaces/types";
import * as z from "zod";
import { getBridgeLogger } from "../logger.js";
import type { ModelEntry } from "../models.js";
import { BUILTIN_DRIVER_CATALOG } from "../registry.js";
import type { AgentConfig, DriverInfo } from "../types.js";
import {
  extractCost,
  extractText,
  extractTokens,
  mapApiKeysToEnv,
  SubprocessCliDriver,
} from "./_subprocess-cli.js";

/**
 * Agent driver for **Goose** (Block) (`goose`), the model-agnostic open-source
 * agent.
 *
 * Spawns `goose run -t "<prompt>" --output-format stream-json --quiet` per turn
 * and maps Goose's stream-json events to the unified {@link AgentEvent} stream.
 * Model-agnostic across 15+ providers; provider/model are forwarded via
 * `GOOSE_PROVIDER` / `GOOSE_MODEL` and provider keys through the environment.
 *
 * @remarks
 * - Goose is a Rust binary (not npm) — install via Block's `download_cli.sh` or
 *   Homebrew.
 * - `--output-format` support is version-dependent; a too-old CLI surfaces as a
 *   `process`-category exit error.
 * - Session resume via `--resume --id <id>`.
 *
 * @see https://github.com/block/goose
 * @docLink packages/bridge/drivers#goose-driver
 */
export class GooseDriver extends SubprocessCliDriver {
  readonly driverInfo: DriverInfo = {
    id: "goose",
    name: "Goose",
    modelAgnostic: true,
    supportsInBandAbort: false,
  };

  protected readonly log: Logger = getBridgeLogger("goose");
  protected readonly defaultBin = "goose";
  protected readonly productName = "Goose";
  protected readonly envPrefix = "GOOSE";

  protected buildArgs(message: string, resumeId: string | undefined): string[] {
    const args = ["run", "-t", message, "--output-format", "stream-json", "--quiet"];
    if (resumeId) args.push("--resume", "--id", resumeId);
    return args;
  }

  protected buildEnv(): Record<string, string> {
    const env = this.baseEnv(["GOOSE_PROVIDER", "GOOSE_MODEL"]);
    if (this.config.provider) env.GOOSE_PROVIDER = this.config.provider;
    if (this.config.model) env.GOOSE_MODEL = this.config.model;
    return { ...env, ...mapApiKeysToEnv(this.config.apiKeys) };
  }

  protected installHint(): string {
    return "Goose is not installed or not on your PATH. Install it from https://block.github.io/goose (download script or Homebrew).";
  }

  protected handleJson(json: any): void {
    switch (json?.type) {
      case "init":
      case "session":
        this.captureSession(json.sessionId ?? json.session_id ?? json.id);
        break;

      case "message":
      case "assistant": {
        const role = json.role ?? json.message?.role ?? "assistant";
        if (role !== "assistant") break;
        const text = extractText(json);
        if (json.done === true || json.final === true) this.pushAssistantEnd(text);
        else this.pushAssistantDelta(text);
        break;
      }

      case "tool_use":
      case "tool_request":
        this.pushToolCall(json.name ?? json.tool ?? "tool", json.input ?? json.args ?? {});
        break;

      case "tool_result":
      case "tool_response":
        this.pushToolResult(
          json.name ?? json.tool ?? "tool",
          json.result ?? json.output ?? json.content,
          Boolean(json.is_error ?? json.error),
        );
        break;

      case "error":
        this.pushError(this.classifyMessage(String(json.message ?? json.error ?? "error")));
        break;

      case "result":
      case "complete":
        this.pushResult({
          tokens: extractTokens(json),
          costUsd: extractCost(json),
          summary: extractText(json) || "Task completed",
        });
        break;
    }
  }

  override async listModels(): Promise<ModelEntry[]> {
    return [];
  }
}

/**
 * `DriverTarget` wrapper for the goose driver. Registered into the plugin
 * registry by `registerBuiltinDrivers()`.
 */
export const gooseDriverTarget: DriverTarget<AgentConfig> = {
  id: "goose",
  displayName: BUILTIN_DRIVER_CATALOG.goose?.name ?? "Goose",
  apiVersion: 1,
  configSchema: z.custom<AgentConfig>(() => true),
  create: async (config, _ctx) => new GooseDriver(config),
};
